import {useEffect, useState} from "react";


export interface VZThinkingStepConfig {
    title: string;
    durationMs: number;
    run?: () => Promise<string | void>;
    substeps?: string[];
    completedSubtitle?: string;
}

export type VZThinkingStepStatus = "pending" | "active" | "complete";

export interface VZThinkingStepState {
    title: string;
    status: VZThinkingStepStatus;
    substepCount: number;
    substepIndex?: number;
    subtitle: string;
}

interface VZThinkingRunnerProgress {
    activeIndex: number;
    substepIndex: number;
    resolvedSubtitles: (string | void)[];
}

export default function useVZThinkingRunner(steps: VZThinkingStepConfig[], onComplete?: () => void): VZThinkingStepState[] {
    const [progress, setProgress] = useState<VZThinkingRunnerProgress>({
        activeIndex: 0,
        substepIndex: 0,
        resolvedSubtitles: [],
    });

    // the idea here is each step waits for a set ms, and steps with a "run" attr ALSO wait for its promise, so a step
    // only finishes when both are done. durationMs is basically a minimum display time so fast requests
    // dont just flash past.
    // steps with substeps spread the durationMs evenly across the substeps
    useEffect(() => {
        const step = steps[progress.activeIndex];
        if (!step) {
            onComplete?.();
            return;
        }

        const substeps = step.substeps ?? [];
        let isCancelled = false;
        let durationTimeout: number | undefined;

        const substepTimeouts = substeps.slice(1).map((_, index) => setTimeout(() => {
            setProgress(current => ({
                ...current,
                substepIndex: index + 1,
            }));
        }, (index + 1) * step.durationMs / substeps.length));

        const durationElapsed = new Promise<void>(resolve => {
            durationTimeout = setTimeout(resolve, step.durationMs);
        });

        Promise.all([durationElapsed, step.run?.()]).then(([_, resolvedSubtitle]) => {
            if (isCancelled) {
                return;
            }
            setProgress(current => ({
                activeIndex: current.activeIndex + 1,
                substepIndex: 0,
                resolvedSubtitles: [...current.resolvedSubtitles, resolvedSubtitle],
            }));
        });

        return () => {
            isCancelled = true;
            clearTimeout(durationTimeout);
            substepTimeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, [progress.activeIndex]);

    return steps.map((step, index): VZThinkingStepState => {
        const substeps = step.substeps ?? [];

        if (index < progress.activeIndex) {
            return {
                title: step.title,
                status: "complete",
                substepCount: substeps.length,
                subtitle: progress.resolvedSubtitles[index] || step.completedSubtitle || "Finished.",
            };
        }

        if (index === progress.activeIndex) {
            return {
                title: step.title,
                status: "active",
                substepCount: substeps.length,
                substepIndex: progress.substepIndex,
                subtitle: substeps.length > 0 ? `${substeps[progress.substepIndex]}...` : "Working...",
            };
        }

        return {
            title: step.title,
            status: "pending",
            substepCount: substeps.length,
            subtitle: "",
        };
    });
}
